import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo_basic.png";
import AppFooter from "../components/AppFooter";
import PublicSiteHeader from "../components/PublicSiteHeader";

export default function FAQPage() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(0);

  const faqItems = useMemo(
    () => [
      {
        id: "ai-career-resilience-audit",
        question: "What is an AI Career Resilience Audit, and how does it future-proof my job?",
        answer:
          "An AI Career Resilience Audit is a personalized career assessment that measures your current job's risk of automation. Instead of just giving you a generic risk score, it analyzes your daily tasks and maps out specific, high-growth career pathways where your unique human experience provides a competitive advantage over artificial intelligence.",
      },
      {
        id: "ai-replace-non-tech-jobs",
        question: "Will AI replace my job if I don't work in the tech industry?",
        answer:
          "Yes, AI is impacting nearly every sector, from hospitality and administration to construction and law. Many professionals believe their jobs are safe until a sudden industry shift occurs. Our job displacement risk assessment replaces uncertainty with data, helping you identify safe pathways before structural changes disrupt your current industry.",
      },
      {
        id: "different-from-career-tests",
        question: "How is Reignite.Spirit different from traditional career aptitude tests?",
        answer:
          "Standard career tests focus heavily on generic personality traits. Our AI career mapping is fundamentally different; we compare your existing technical and soft skills against real-world O*NET labor data and localized market trends. We don't just provide a psychological profile—we give you a strategic, data-backed roadmap to an AI-resilient job.",
      },
      {
        id: "higher-salary-ai-resilient-skills",
        question: "Can learning AI-resilient skills help me get a higher salary?",
        answer:
          "Absolutely. The current labor market offers a significant wage premium for workers who transition into roles that require a mix of human expertise and AI-awareness. By identifying the exact skill gaps between your current role and a future-proof job, you can focus your training on high-value skills that command better pay.",
      },
      {
        id: "blue-collar-onsite-workers",
        question: "Is an AI career pivot strategy useful for blue-collar and onsite workers?",
        answer:
          "Yes. While generative AI heavily impacts office-based roles, the job market is evolving for everyone. Our platform is inclusive of all professions. For blue-collar, practical, or onsite roles, we identify pathways that prioritize physical dexterity, complex problem-solving, and on-the-ground management—highly valued skills that robots cannot replicate.",
      },
      {
        id: "specific-city-market-data",
        question: "Do you provide localized job market data for my specific city?",
        answer:
          "Yes. Whether you are searching for a career pivot in the UAE, the USA, India, or anywhere else, our logic is highly localized. A smart career transition in a fast-growing tech hub requires a different strategy than one in a traditional manufacturing center. Your report identifies pathways and training options available right where you live.",
      },
      {
        id: "snapshot-vs-deep-audit",
        question: "Should I choose the Snapshot or the Deep Audit for my career change?",
        answer:
          "The Snapshot is a great starting point if you want a rapid AI-risk check and a suggestion for one primary career path. However, if you are serious about making a successful transition, The Deep Audit is the recommended choice. It delivers three comprehensive career pathways, a localized skill-gap analysis, a weekly action plan, and exclusive access to our ATS Optimizer to ensure your new CV bypasses automated hiring filters.",
      },
      {
        id: "skill-gap-career-pivot",
        question: "How do I identify and fill my skill gaps for a career pivot?",
        answer:
          "Once your ideal future-proof role is identified, our report generates a personalized Skill Gap Analysis. We don't just tell you what you are missing; we recommend practical, localized micro-skilling courses and certifications. This allows you to gain the exact skills you need efficiently, without putting your life on hold.",
      },
      {
        id: "degree-needed-ai-safe-career",
        question: "Do I need a new college degree to transition into an AI-safe career?",
        answer:
          "Not necessarily. Employers are increasingly shifting toward skills-based hiring rather than requiring traditional four-year degrees. Our reports highlight targeted micro-skilling paths and short-term certifications that prove your competency, getting you ready for your next role in the most cost-effective and time-efficient way possible.",
      },
      {
        id: "human-centric-skills",
        question: "What are human-centric skills, and why are they AI-resistant?",
        answer:
          "Human-centric skills are abilities that artificial intelligence cannot easily copy. These include complex empathy, ethical decision-making, specialized physical dexterity, and team leadership. Our audit helps you pinpoint which of these high-value skills you already possess and teaches you how to highlight them to employers.",
      },
      {
        id: "report-generation-time",
        question: "How long does it take to generate my AI Career Resilience Report?",
        answer:
          "Your personalized career assessment is generated almost instantly. After you complete a simple, 10-to-15-minute questionnaire, our system processes your data and delivers the results directly to your dashboard and email.",
      },
      {
        id: "reassess-ai-risk-frequency",
        question: "How often should I reassess my job's AI displacement risk?",
        answer:
          "Because technology and local labor markets change rapidly, we recommend updating your AI Career Resilience Audit every six months. Regular assessments ensure your career pivot strategy remains aligned with the latest AI advancements and emerging job opportunities in your target city.",
      },
      {
        id: "localized-labor-data",
        question: "How does Reignite Spirit handle localized labor market data?",
        answer:
          "Unlike standard platforms utilizing generic job descriptions, Reignite Spirit isolates career vulnerabilities against real-time city-specific hiring indices and regional AI adoption datasets. This structural approach guarantees that mapped career pathways match active local commercial demands and local skill gaps.",
      },
      {
        id: "adgm-compliance-framework",
        question: "Is the platform registered or compliant with regional standards?",
        answer:
          "Yes. Reignite Spirit operates formally under corporate registration and jurisdiction of the Abu Dhabi Global Market (ADGM), United Arab Emirates. Our data compliance structures, calculation logic, and transition metrics adhere to established corporate standards.",
      },
    ],
    []
  );

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const toggleFaq = (index) => {
    setOpenIndex((prev) => (prev === index ? -1 : index));
  };

  return (
    <div className="min-h-screen bg-[#11111f] text-slate-300">
      <Helmet>
        <title>FAQ | Reignite Spirit</title>
        <meta
          name="description"
          content="Frequently asked questions about Reignite Spirit's AI Career Resilience Audit, career pivot strategy, skill gap analysis, localized labor market data, and regional compliance."
        />
        <link rel="canonical" href="https://www.reignitespirit.com/faq" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="FAQ | Reignite Spirit" />
        <meta
          property="og:description"
          content="Explore answers about AI career resilience, job displacement risk, skill gap analysis, localized labor data, and future-proof career planning."
        />
        <meta property="og:url" content="https://www.reignitespirit.com/faq" />
        <meta property="og:image" content="https://www.reignitespirit.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <PublicSiteHeader active="faq" />

      <main className="relative overflow-hidden">
        <section className="relative py-20 sm:py-24 border-b border-slate-800 bg-gradient-to-b from-[#1a1a2e] to-[#11111f]">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_rgba(203,1,189,0.25),_transparent_45%)]" />
          <div className="max-w-5xl mx-auto px-4 relative z-10 text-center">
            <span className="inline-block px-4 py-1 rounded-full bg-[#cb01bd]/10 text-[#cb01bd] font-bold text-sm mb-4">
              SUPPORT
            </span>

            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6">
              Frequently Asked Questions
            </h1>

            <p className="max-w-3xl mx-auto text-lg text-slate-400 leading-relaxed">
              Everything you need to know about the AI Career Resilience Audit,
              career pivots, job risk, and how Reignite Spirit helps you build a
              future-proof roadmap.
            </p>
          </div>
        </section>

        <section className="py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 ai-accessible-faq-container">
            <div className="space-y-4">
              {faqItems.map((item, index) => {
                const isOpen = openIndex === index;

                return (
                  <article
                    key={item.id}
                    className={`rounded-2xl border transition-all overflow-hidden ${
                      isOpen
                        ? "border-[#cb01bd] bg-[#1a1a2e] shadow-[0_0_20px_rgba(203,1,189,0.12)]"
                        : "border-slate-700 bg-[#151526]"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(index)}
                      className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                      aria-expanded={isOpen}
                      aria-controls={`${item.id}-answer`}
                    >
                      <span className="text-base sm:text-lg font-bold text-white">
                        {item.question}
                      </span>

                      <span
                        className={`text-2xl font-light transition-transform ${
                          isOpen ? "rotate-45 text-[#cb01bd]" : "text-slate-400"
                        }`}
                      >
                        +
                      </span>
                    </button>

                    <div
                      id={`${item.id}-answer`}
                      className={`px-6 pb-6 ${isOpen ? "block" : "sr-only"}`}
                    >
                      <div className="h-px bg-slate-700 mb-4" />

                      <h3 id={item.id} className="sr-only">
                        {item.question}
                      </h3>

                      <p className="text-slate-300 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
              <div className="sr-only ai-accessible-faq-container">
  {faqItems.map((item) => (
    <section key={`plain-${item.id}`}>
      <h3 id={`${item.id}-plain`}>{item.question}</h3>
      <p>{item.answer}</p>
    </section>
  ))}
</div>

            <div className="mt-12 text-center">
              <button
                onClick={() => navigate("/landing#pricing")}
                className="px-8 py-4 bg-[#cb01bd] hover:bg-[#e94ce0] text-white font-bold rounded-xl shadow-lg shadow-[#cb01bd]/30 transition-all"
              >
                Get My Audit
              </button>
            </div>
          </div>
        </section>
      </main>

      <AppFooter variant="landing" logoSrc={logo} />
    </div>
  );
}